import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  HeadingLevel,
  ExternalHyperlink,
  WidthType,
  BorderStyle,
} from "docx";
import { saveAs } from "file-saver";
import { isTableLine, isTableSeparator, parseTableRows } from "../parser/markdown-parser";
import { parseBoldSegments } from "../parser/text-parser";
import type { ExportReportArguments } from "../types";

/**
 * 마크다운 헤딩 라인을 파싱하여 Word 헤딩 레벨과 텍스트 추출
 * 1. # 개수에 따라 HEADING_1~5로 매핑
 * 2. # 기호 제거 후 텍스트 반환
 * 파라미터:
 * - line: 마크다운 헤딩 라인 문자열
 * 예상응답: 헤딩이면 { level, text }, 아니면 null
 */
const parseHeadingLevel = (
  line: string
): { level: typeof HeadingLevel[keyof typeof HeadingLevel]; text: string } | null => {
  if (line.startsWith("# ")) return { level: HeadingLevel.HEADING_1, text: line.substring(2) };
  if (line.startsWith("## ")) return { level: HeadingLevel.HEADING_2, text: line.substring(3) };
  if (line.startsWith("### ")) return { level: HeadingLevel.HEADING_3, text: line.substring(4) };
  if (line.startsWith("#### ")) return { level: HeadingLevel.HEADING_4, text: line.substring(5) };
  if (line.startsWith("##### ")) return { level: HeadingLevel.HEADING_5, text: line.substring(6) };
  return null;
};

/**
 * 텍스트를 파싱하여 Word TextRun 배열로 변환
 * 1. parseBoldSegments로 텍스트를 일반/볼드 세그먼트로 분리
 * 2. 각 세그먼트를 TextRun으로 변환 (폰트 크기 20pt)
 * 파라미터:
 * - text: 변환할 텍스트 문자열
 * 예상응답: TextRun 배열
 */
const parseBoldText = (text: string): TextRun[] =>
  parseBoldSegments(text).map((seg) => new TextRun({ text: seg.text, bold: seg.bold, size: 20 }));

/**
 * 테이블 셀 생성
 * 1. 셀 텍스트에 • 기호가 있으면 각 항목을 별도 Paragraph로 분리하여 리스트 생성
 * 2. • 기호가 없으면 parseBoldText로 일반/볼드 텍스트 처리
 * 3. 헤더 셀인 경우 회색 배경색 적용
 * 파라미터:
 * - cellText: 셀 내용 텍스트
 * - isHeader: 헤더 셀 여부
 * 예상응답: TableCell 객체
 */
const createTableCell = (cellText: string, isHeader: boolean): TableCell => {
  const bulletPoints = cellText.split(/(?=• )/g).filter((part) => part.trim());

  const cellParagraphs: Paragraph[] =
    bulletPoints.length > 1
      ? bulletPoints.map(
          (bullet) =>
            new Paragraph({
              text: bullet.replace(/^• /, "").trim(),
              bullet: { level: 0 },
              spacing: { before: 100, after: 100 },
            })
        )
      : [new Paragraph({ children: parseBoldText(cellText) })];

  return new TableCell({
    children: cellParagraphs,
    shading: isHeader ? { fill: "E0E0E0" } : undefined,
  });
};

/**
 * 마크다운 테이블 라인들을 Word Table 객체로 변환
 * 1. parseTableRows로 라인들을 2차원 배열로 파싱
 * 2. 각 행을 TableRow로, 각 셀을 createTableCell로 변환
 * 3. 테이블 너비 100%, 테두리 스타일 설정
 * 파라미터:
 * - lines: 마크다운 테이블 라인 배열
 * 예상응답: Table 객체 또는 null (행이 없으면)
 */
const parseMarkdownTable = (lines: string[]): Table | null => {
  const rows = parseTableRows(lines);
  if (rows.length === 0) return null;

  return new Table({
    rows: rows.map(
      (cells, index) =>
        new TableRow({
          children: cells.map((cellText) => createTableCell(cellText, index === 0)),
        })
    ),
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 1 },
      bottom: { style: BorderStyle.SINGLE, size: 1 },
      left: { style: BorderStyle.SINGLE, size: 1 },
      right: { style: BorderStyle.SINGLE, size: 1 },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
      insideVertical: { style: BorderStyle.SINGLE, size: 1 },
    },
  });
};

/**
 * 마크다운 콘텐츠를 Word 문서로 변환하여 다운로드
 * 1. 콘텐츠를 줄 단위로 분리하여 순회
 * 2. 각 라인 타입에 따라 처리:
 *    - 빈 줄: 빈 Paragraph 추가
 *    - 테이블 라인: 연속된 테이블 라인 수집 후 parseMarkdownTable로 변환
 *    - 헤딩: parseHeadingLevel로 파싱하여 헤딩 Paragraph 생성
 *    - 리스트(- 또는 *): 리스트 Paragraph 생성
 *    - 일반 텍스트: 일반 Paragraph 생성
 * 3. references가 있으면 "References" 헤딩과 하이퍼링크 리스트 추가
 * 4. Document 생성 후 Packer.toBlob으로 변환하여 다운로드
 * 파라미터:
 * - content: 마크다운 형식의 리포트 본문 내용
 * - references: 참고문헌 배열 (선택적, 기본값: [])
 * - fileName: 저장될 파일명 (확장자 제외)
 */
export const exportWord = async ({
  content,
  references = [],
  fileName,
}: ExportReportArguments): Promise<void> => {
  const paragraphs: (Paragraph | Table)[] = [];
  const lines = content.split("\n");

  for (let i = 0; i < lines.length; i++) {
    const trimmedLine = lines[i].trim();

    if (!trimmedLine) {
      paragraphs.push(new Paragraph({ text: "" }));
      continue;
    }

    if (isTableLine(trimmedLine)) {
      const tableLines: string[] = [];
      const startIndex = i;

      while (i < lines.length && isTableLine(lines[i].trim())) {
        tableLines.push(lines[i].trim());
        i++;
      }

      if (tableLines.length >= 2 && isTableSeparator(tableLines[1])) {
        const table = parseMarkdownTable(tableLines);
        if (table) {
          paragraphs.push(table);
          i--;
          continue;
        }
      }

      i = startIndex;
      paragraphs.push(new Paragraph({ children: [new TextRun({ text: trimmedLine, size: 24 })] }));
      continue;
    }

    const heading = parseHeadingLevel(trimmedLine);
    if (heading) {
      paragraphs.push(new Paragraph({ text: heading.text, heading: heading.level }));
      continue;
    }

    if (trimmedLine.startsWith("- ") || trimmedLine.startsWith("* ")) {
      paragraphs.push(new Paragraph({ text: trimmedLine.substring(2), bullet: { level: 0 } }));
      continue;
    }

    paragraphs.push(new Paragraph({ children: [new TextRun({ text: trimmedLine, size: 24 })] }));
  }

  if ((references?.length ?? 0) > 0) {
    paragraphs.push(new Paragraph({ text: "" }));
    paragraphs.push(new Paragraph({ text: "References", heading: HeadingLevel.HEADING_2 }));
    references.forEach((reference) => {
      paragraphs.push(
        new Paragraph({
          children: [
            new ExternalHyperlink({
              children: [new TextRun({ text: reference.title, style: "Hyperlink" })],
              link: reference.url,
            }),
          ],
        })
      );
    });
  }

  const blob = await Packer.toBlob(
    new Document({ sections: [{ properties: {}, children: paragraphs }] })
  );
  saveAs(blob, `${fileName}.docx`);
};